import { MatrixError } from 'matrix-js-sdk/src/matrix';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import logger from 'utils/logging/faro';
import { logAndSendToSentry } from 'utils/utils';

import useMxCookies from './useMxCookies';

/**
 * Use logout hook for logging out of the matrix account
 * Clears the matrix session, indexDB, cookies and the matrix client
 * Reloads the page after logging out
 * @returns logout function
 */
const useLogout = () => {
  const { matrixClient, setMxAvatarUrl } = useMatrixContext();
  const { clearCookies } = useMxCookies();

  const deleteMatrixSession = () => {
    logger.info('Deleting matrix session');
    const deviceId = matrixClient.current?.getDeviceId();
    if (!matrixClient.current || !deviceId) {
      return logger.info('Missing variables in deleteMatrixSession', {
        hasMatrixClient: !!matrixClient.current,
        deviceId,
      });
    }

    const authPayload = {
      type: 'm.login.token',
      token: matrixClient.current.getAccessToken(),
    };

    return matrixClient.current.deleteDevice(deviceId, authPayload).catch((error) => {
      // NOTE: don't send to sentry if device is already deleted
      if (error instanceof MatrixError && error.httpStatus === 404 && error.errcode === 'M_UNKNOWN') return;

      logAndSendToSentry('Failed to delete matrix session', error);
    });
  };

  const clearAllIndexedDB = async () => {
    if (window.indexedDB && window.indexedDB.databases) {
      const dbs = await window.indexedDB.databases();
      await Promise.all(dbs.map((db) => db.name && window.indexedDB.deleteDatabase(db.name)));
    }
  };

  const logout = async (reload = true) => {
    logger.info('Logout started');
    await deleteMatrixSession();
    setMxAvatarUrl('');

    logger.info('Clearing cookies');
    clearCookies();

    if (matrixClient.current) {
      logger.info('Logging out of the matrixClient.current and clearing it');
      await matrixClient.current.logout(true);
      await matrixClient.current.clearStores();
      matrixClient.current.stopClient();
      matrixClient.current = null;
    } else {
      logger.info('No Matrix matrixClient.current found during the logout');
    }

    logger.info('Clearing all IndexedDB');
    await clearAllIndexedDB();

    logger.info('Logout completed, reloading page');
    if (reload) window.location.reload();
  };

  return logout;
};

export default useLogout;
